// A cart line's effective unit price: the chosen option's price if the product
// has a selected variant, otherwise the product's offer price / regular price,
// plus each selected addon's price × its own quantity.
export function lineUnitPrice(item) {
  const base = item.option
    ? Number(item.option.price)
    : Number(item.product.offer_price ?? item.product.price ?? 0);
  const addonsTotal = (item.addons || []).reduce(
    (sum, a) => sum + Number(a.price || 0) * (a.quantity || 1),
    0
  );
  return base + addonsTotal;
}

export function lineTotal(item) {
  return lineUnitPrice(item) * item.quantity;
}

export function cartTotal(cart) {
  return cart.reduce((sum, item) => sum + lineTotal(item), 0);
}

export function formatPrice(value, currency = "") {
  const n = Number(value || 0);
  return currency ? `${currency} ${n.toFixed(2)}` : n.toFixed(2);
}

// A dine_in/take_away order is never required to give a name, so a blank one
// just means "walk-in" — "Guest" reads as expected instead of blank. Delivery
// keeps showing blank if somehow still empty, since a name is actually
// required there.
export function displayCustomerName(order) {
  if (order?.name) return order.name;
  return order?.order_type !== "delivery" ? "Guest" : "";
}

// product.description comes from denzo-admin's rich-text editor as raw HTML
// (e.g. "<p>...</p><br /><h2>...</h2>"), not plain text — matches
// denzo-web-app's own DescriptionView.js, which strips it to plain text
// rather than rendering it, so this does the same for consistency across the
// app family (and to avoid dangerouslySetInnerHTML on store-authored content).
export function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

// kot_printed_qty > 0 means at least one unit of this line has already gone to
// the kitchen — reducing or removing it needs a real reason. A line that was
// never printed can be changed freely without bothering the waiter for one.
export function isKotPrinted(orderItem) {
  return Number(orderItem?.kot_printed_qty ?? orderItem?.kotPrintedQty ?? 0) > 0;
}

// A line needs justification if it's an already-KOT-printed existing item that
// either got fully removed (present in cancelledLines) or had its quantity
// reduced below what it started this edit session at (still in `lines`, but
// quantity < originalQuantity). Used both to decide whether to prompt at all,
// and to build the reason-carrying payload once one's been given.
export function linesNeedingReason(lines, cancelledLines) {
  const reducedLines = (lines || []).filter(
    (l) => l.orderItemId && l.originalQuantity != null && l.quantity < l.originalQuantity && isKotPrinted(l)
  );
  const removedLines = (cancelledLines || []).filter((c) => isKotPrinted(c));
  return { reducedLines, removedLines, any: reducedLines.length > 0 || removedLines.length > 0 };
}

// The lines a Save-triggered KOT print will actually put in front of the
// kitchen: a new line (no orderItemId yet, kotPrintedQty 0) counts in full,
// an existing line counts only the amount raised above what's already been
// printed — mirrors the backend's own quantity - kot_printed_qty calculation
// (CommonController@getKOTPrintLine), so this always matches the ticket.
export function kotPrintLines(lines) {
  return (lines || [])
    .map((l) => ({ name: l.product.name, qty: l.quantity - (l.kotPrintedQty || 0) }))
    .filter((l) => l.qty > 0);
}

// order_items entries for placeOrder: an item with an existing orderItemId
// gets updated in place (quantity/notes), one without is a brand new line.
// cancelledLines (id + cancel_type/cancel_reason) appends status:2 entries.
// sharedReason ({cancelType, cancelReason}) is applied to whichever kept/
// cancelled lines actually need one (see linesNeedingReason) — a line that
// was never KOT-printed gets a fixed, silent default instead, matching the
// "only ask when it matters" rule.
function buildOrderItems(lines, cancelledLines, sharedReason) {
  const kept = (lines || []).map((item) => {
    const reduced = item.orderItemId && item.originalQuantity != null && item.quantity < item.originalQuantity;
    const needsReason = reduced && isKotPrinted(item);
    const base = item.orderItemId
      ? { id: item.orderItemId, product: { id: item.product.id }, quantity: item.quantity, notes: item.notes || "" }
      : {
          product: { id: item.product.id },
          option: item.option ? { id: item.option.id } : null,
          addons: (item.addons || []).map((a) => ({ id: a.id, quantity: a.quantity || 1 })),
          quantity: item.quantity,
          notes: item.notes || "",
        };
    if (needsReason && sharedReason) {
      base.cancel_type = sharedReason.cancelType;
      base.cancel_reason = sharedReason.cancelReason;
    }
    return base;
  });

  const cancelled = (cancelledLines || []).map((c) => ({
    id: c.orderItemId,
    status: 2,
    cancel_type: isKotPrinted(c) && sharedReason ? sharedReason.cancelType : "cancel",
    cancel_reason: isKotPrinted(c) && sharedReason ? sharedReason.cancelReason : "Removed before kitchen print",
  }));

  return [...kept, ...cancelled];
}

// Builds the JSON payload denzo-api's pos/v1/orders/place expects under its
// `order` field (see OrderController@placeOrder). store_table_id is only
// honored by the backend when order_type === 'dine_in'; delivery_address only
// when order_type === 'delivery' — take_away needs neither. name/discount/
// delivery_charge are always included (even as null/"") since the backend
// reads them directly off the decoded order without an isset()/?? guard —
// omitting one 500s the request.
//
// store_customer_id/contact_number matter more than that: OrderController@
// placeOrder requires contact_number whenever store_customer_id is null (if
// the store's "customer_details_required" setting is on) for delivery orders
// only — dine_in and take_away are walk-in orders with no contact to collect,
// matching Cart.js's own delivery-only check. For a brand-new order (Cart.js) there's
// no existing customer link, so both are fine left blank/collected fresh. For
// editing an already-placed order (LiveOrders.js) they must be the order's
// *existing* storeCustomerId/contactNumber — sending null/"" here for an
// order that already has a linked customer fails that same validation even
// though the customer is right there, since placeOrder validates whatever
// this payload says, not what's already on the order in the database.
// cancelledLines/sharedReason are also edit-only (the order itself must be in
// 'created' or 'modifying' status for placeOrder to accept it, so a
// 'processing' order needs POST orders/modify called first).
export function buildOrderPayload({
  cart,
  orderType,
  table,
  deliveryAddress,
  contactNumber,
  storeCustomerId,
  customerName,
  orderNote,
  noOfPeople,
  existingOrderId,
  cancelledLines,
  sharedReason,
}) {
  return {
    id: existingOrderId || undefined,
    order_type: orderType,
    store_customer_id: storeCustomerId ?? null,
    name: customerName || "",
    discount: null,
    delivery_charge: null,
    store_table_id: orderType === "dine_in" && table ? table.id : null,
    delivery_address: orderType === "delivery" ? deliveryAddress || "" : "",
    contact_number: contactNumber || "",
    order_note: orderNote || "",
    no_of_people: noOfPeople || 1,
    offline_payment_option: "cash",
    order_items: buildOrderItems(cart, cancelledLines, sharedReason),
    extra_order_items: [],
  };
}

// Converts a fetched order's order_items (denzo-api's shape) into this app's
// internal edit-line shape, so the same qty-stepper / remove UI used for a new
// cart can be reused for editing an existing order.
export function orderItemsToEditLines(orderItems) {
  return (orderItems || [])
    .filter((oi) => oi.status !== 2) // already-cancelled lines aren't editable
    .map((oi) => ({
      cartId: `existing_${oi.id}`,
      orderItemId: oi.id,
      product: oi.product,
      option: oi.option ? { id: oi.product_option_id, name: oi.option.name, price: oi.option.price } : null,
      addons: Array.isArray(oi.addons) ? oi.addons : [],
      quantity: oi.quantity,
      originalQuantity: oi.quantity, // fixed snapshot — quantity mutates as the waiter edits, this doesn't
      notes: typeof oi.notes === "string" ? oi.notes : "",
      kotPrintedQty: Number(oi.kot_printed_qty || 0),
      // The actually-charged price recorded on the order item (may differ from
      // the product's current price due to custom pricing/promos at order time)
      // — prefer this over recomputing from live product data for display.
      unitPrice: Number(oi.unit_price || 0),
      totalPrice: Number(oi.total_price || 0),
    }));
}
